import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, X, Plus, AlertTriangle } from 'lucide-react';
import Icon from 'shared/components/Icon';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import {
  activitySearchWrapperClass, activitySearchInputClass,
  tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass, activityNeutralButtonClass,
} from 'features/activity/styles/dataTableStyles';
import { listAlerts, setAlertEnabled, deleteAlert } from 'features/alerts/api/alertsApi';
import { timeAgo, STATE_LABEL, STATE_TIP, STATE_COLOR } from 'features/alerts/format';
import { useConfirmDelete } from 'features/alerts/components/AlertActions';
import BulkBar from 'shared/components/tables/BulkBar';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import NewAlertDialog from 'features/alerts/components/NewAlertDialog';
import { openAsk } from 'shared/askDraft';

/**
 * The alerts list, in the same grammar as the Evals list: search on the
 * left, New alert on the right; a row is the alert's state, what it watches
 * (the Traces icon for a run or span, the eval icon), its condition, how
 * often and when it last triggered. A click opens it, and every control lives
 * there. Ticking rows brings up Enable, Disable and Delete for all of them.
 */
export default function AlertsListPage() {
  const { team } = useParams();
  const navigate = useNavigate();
  useBreadcrumb([{ label: 'Alerts' }]);

  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const confirmDelete = useConfirmDelete();
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const [choosing, setChoosing] = useState(false);
  const closeChooser = useCallback(() => setChoosing(false), []);

  const load = useCallback(async () => {
    try { setRows(await listAlerts()); setError(''); }
    catch (e) { setError(e?.response?.data?.error || e.message); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // On before paused, then by recency.
  const visible = useMemo(() => {
    const list = (rows || []).filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));
    const rank = (r) => (r.enabled ? 0 : 1);
    return [...list].sort((a, b) => rank(a) - rank(b) || new Date(b.updated_at) - new Date(a.updated_at));
  }, [rows, q]);

  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  // A reload (or a search) can drop rows the selection still holds.
  useEffect(() => { setPicked((cur) => (rows ? new Set([...cur].filter((id) => rows.some((r) => r.id === id))) : cur)); }, [rows]);
  const chosen = (rows || []).filter((r) => picked.has(r.id));
  const allPicked = visible.length > 0 && visible.every((r) => picked.has(r.id));
  const toggleOne = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  /** One call per alert; what failed is said, what worked is kept. */
  const each = async (targets, fn) => {
    setBusy(true);
    const res = await Promise.allSettled(targets.map((r) => fn(r.id)));
    const failed = res.filter((x) => x.status === 'rejected');
    setPicked(new Set());
    await load();
    if (failed.length) setError(failed[0].reason?.response?.data?.error || failed[0].reason?.message || 'Something failed');
    setBusy(false);
  };
  const setEnabled = (enabled) => each(chosen.filter((r) => r.enabled !== enabled), (id) => setAlertEnabled(id, enabled));
  const removeChosen = async () => {
    if (!chosen.length || !(await confirmDelete({ name: chosen[0].name, count: chosen.length }))) return;
    await each(chosen, (id) => deleteAlert(id));
  };

  const open = (r) => navigate(`/${team}/alerts/${r.id}`);

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* The same header as Evals: search on the left, the one action on the right. */}
      <div className="flex shrink-0 flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">
        <div className={`${activitySearchWrapperClass} w-[240px]`}>
          <Search size={14} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search alerts…" aria-label="Search alerts" className={activitySearchInputClass} />
          {q && (
            <button type="button" onClick={() => setQ('')} className="flex-none text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]" aria-label="Clear search">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex-1" />
        {canWrite && (
          <button type="button" onClick={() => setChoosing(true)}
            className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover">
            <Plus size={13} /> New alert
          </button>
        )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col px-4 pb-8 pt-3">
          {error && <div className="mb-3 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-3 text-[12px] text-danger">{error}</div>}
          {rows === null && <PageLoader label="Loading alerts" />}
          {rows !== null && rows.length === 0 && !error && (
            <div className="flex h-48 items-center justify-center gap-3 rounded-md border border-[color:var(--border-default)] bg-surface px-6 text-center text-[12px] text-[color:var(--text-muted)]">
              No alerts yet.
              {canWrite && (
                <button type="button" onClick={() => setChoosing(true)}
                  className="inline-flex h-8 items-center gap-1 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover">
                  <Plus size={13} /> New alert
                </button>
              )}
            </div>
          )}
          {rows !== null && rows.length > 0 && (
            <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left">
                  <thead>
                    <tr className={tableHeaderRowClass}>
                      <th scope="col" className={`${tableHeaderCellClass} w-8`}>
                        {canWrite && (
                          <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select all'} title={allPicked ? 'Clear the selection' : 'Select all'}
                            onChange={() => setPicked(allPicked ? new Set() : new Set(visible.map((r) => r.id)))} />
                        )}
                      </th>
                      <th scope="col" className={tableHeaderCellClass}>Name</th>
                      <th scope="col" className={tableHeaderCellClass}>Condition</th>
                      <th scope="col" className={`${tableHeaderCellClass} w-[100px]`}>Last 30d</th>
                      <th scope="col" className={`${tableHeaderCellClass} w-[130px]`}>Last triggered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => {
                      const state = r.enabled ? 'on' : 'paused';
                      return (
                        <tr key={r.id} className={`${tableRowClass} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]`}
                          tabIndex={0} aria-label={`Open ${r.name}`} onClick={() => open(r)}
                          onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(r); } }}>
                          <td className={`${tableDataCellClass} w-8`} onClick={(e) => e.stopPropagation()}>
                            {canWrite && <input type="checkbox" checked={picked.has(r.id)} aria-label={`Select ${r.name}`} onChange={() => toggleOne(r.id)} />}
                          </td>
                          <td className={`${tableDataCellClass} max-w-0 w-[34%]`}>
                            <span className="flex min-w-0 items-center gap-2">
                              <StateDot state={state} />
                              <ScopeIcon row={r} />
                              <span className="truncate font-medium">{r.name}</span>
                              {/* An alert nobody is receiving looks exactly like a healthy one otherwise. */}
                              {r.last_delivery_failed && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded bg-danger-soft px-1.5 py-0.5 text-[10px] font-medium text-danger"
                                  title="The last notification could not be delivered: check where this alert sends.">
                                  <AlertTriangle className="h-3 w-3" aria-hidden /> Not delivered
                                </span>
                              )}
                            </span>
                          </td>
                          <td className={`${tableDataCellClass} max-w-[320px]`}>
                            <span className="block truncate text-[color:var(--text-secondary)]" title={r.condition}>
                              {r.condition || '—'}
                              {r.agents_count > 0 ? <span className="text-[color:var(--text-muted)]" title="Agents that start when this fires"> · {r.agents_count} agent{r.agents_count === 1 ? '' : 's'}</span> : null}
                            </span>
                          </td>
                          <td className={`${tableDataCellClass} tabular-nums`}>
                            {r.triggered_30d ? <span className="font-medium">{r.triggered_30d}</span> : <span className="text-[color:var(--text-disabled)]">0</span>}
                          </td>
                          <td className={`${tableDataCellClass} whitespace-nowrap text-[color:var(--text-muted)]`} title={r.last_triggered_at ? new Date(r.last_triggered_at).toLocaleString() : 'Never triggered'}>
                            {r.last_triggered_at ? timeAgo(r.last_triggered_at) : <span className="text-[color:var(--text-disabled)]">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {visible.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-10 text-center text-[12px] text-[color:var(--text-muted)]">Nothing matches “{q}”.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
        <BulkBar count={canWrite ? picked.size : 0} label="Selected alerts" onClear={() => setPicked(new Set())} actions={[
          { label: 'Enable', disabled: busy || !chosen.some((r) => !r.enabled), onClick: () => setEnabled(true) },
          { label: 'Disable', disabled: busy || !chosen.some((r) => r.enabled), onClick: () => setEnabled(false) },
          { label: 'Delete', disabled: busy, onClick: removeChosen, danger: true },
        ]} />
      <NewAlertDialog open={choosing} onClose={closeChooser}
        onPick={(t) => { setChoosing(false); navigate(`/${team}/alerts/new?template=${encodeURIComponent(t.id)}`); }}
        onScratch={() => { setChoosing(false); navigate(`/${team}/alerts/new`); }}
        onAsk={() => { setChoosing(false); openAsk('Create an alert that '); }} />
    </main>
  );
}

/** On is a green dot, paused a hollow one. */
function StateDot({ state }) {
  const hollow = state === 'paused';
  return (
    <span role="img" aria-label={STATE_LABEL[state]} title={STATE_TIP[state]}
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${hollow ? 'border border-[color:var(--text-disabled)]' : ''}`}
      style={hollow ? undefined : { backgroundColor: STATE_COLOR[state] }} />
  );
}

const SCOPE_ICON = { run: 'timeline', span: 'span', eval: 'eval' };
const SCOPE_LABEL = { run: 'Run', span: 'Span', eval: 'Eval' };

/** What it watches, as the Traces icon for it (run, span) or the eval icon, named on hover. */
function ScopeIcon({ row }) {
  const label = row.scope === 'eval' ? `Eval · ${row.eval_name || 'deleted eval'}` : `Every ${String(SCOPE_LABEL[row.scope] || row.scope).toLowerCase()}`;
  return (
    <span className="inline-flex shrink-0 text-[color:var(--text-muted)]" title={label} role="img" aria-label={label}>
      <Icon name={SCOPE_ICON[row.scope] || 'timeline'} size={14} color="currentColor" />
    </span>
  );
}
